import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from './api'

/**
 * Banner global de aviso/manutenção. Singleton no back — sempre 1
 * linha em `system_announcement`.
 *
 * Endpoint público (`/api/system/announcement`) é pollado pelo
 * MaintenanceBanner a cada 60s. Quando `active=true`, o componente
 * renderiza um banner no topo da página com a cor do `severity`.
 */
export type AnnouncementSeverity = 'info' | 'warning' | 'critical'

/** Resposta do endpoint público. */
export interface PublicAnnouncement {
  active: boolean
  message: string
  severity: AnnouncementSeverity
  updatedAt: string
}

/** Resposta do endpoint admin (inclui auditoria). */
export interface AdminAnnouncement extends PublicAnnouncement {
  id: string
  createdAt: string
  updatedBy: string | null
}

export interface UpdateAnnouncementDTO {
  message?: string
  active?: boolean
  severity?: AnnouncementSeverity
}

// ──────────────────────────────────────────────────────────────────────
// Público — polled pelo banner.
// Polling 60s · staleTime curto pra não fazer flash quando admin desliga.

export function usePublicAnnouncement() {
  return useQuery({
    queryKey: ['system-announcement', 'public'],
    queryFn: async () => {
      const response = await api.get<PublicAnnouncement>('/system/announcement')
      return response.data
    },
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  })
}

// ──────────────────────────────────────────────────────────────────────
// Admin — só super_admin enxerga (back já enforça via guard).

export function useAdminAnnouncement() {
  return useQuery({
    queryKey: ['system-announcement', 'admin'],
    queryFn: async () => {
      const response = await api.get<AdminAnnouncement>('/admin/system/announcement')
      return response.data
    },
  })
}

export function useUpdateAnnouncement() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (data: UpdateAnnouncementDTO) => {
      const response = await api.put<AdminAnnouncement>('/admin/system/announcement', data)
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['system-announcement'] })
    },
  })
}

// ──────────────────────────────────────────────────────────────────────
// Status do client secret do Microsoft SSO (super_admin) — pra renovar
// antes de vencer e o login 365 não parar do nada.

export interface MicrosoftSecretStatus {
  configured: boolean
  status: 'ok' | 'warning' | 'expired' | 'unknown'
  hint?: string | null
  expiresAt?: string | null
  daysRemaining?: number | null
  reason?: 'forbidden' | 'error'
}

export function useMicrosoftSecretStatus() {
  return useQuery({
    queryKey: ['microsoft-secret-status'],
    queryFn: async () => {
      const response = await api.get<MicrosoftSecretStatus>('/admin/system/microsoft-secret')
      return response.data
    },
    staleTime: 1000 * 60 * 30,
  })
}
